import React, { useEffect, useState, useCallback } from "react";
import { Check, X, RefreshCw, Search } from "lucide-react";
import api from "../../lib/api";
import AdminLayout from "./AdminLayout";

const fmt = (n) =>
  Number(n || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const statusStyle = {
  Pending: "text-amber-400 bg-amber-400/10",
  Completed: "text-emerald-400 bg-emerald-400/10",
  Rejected: "text-rose-400 bg-rose-400/10",
};

const AdminWithdrawals = () => {
  const [items, setItems] = useState([]);
  const [statusFilter, setStatusFilter] = useState("Pending");
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [rowBusy, setRowBusy] = useState({});
  const [payFor, setPayFor] = useState(null);
  const [payTx, setPayTx] = useState("");
  const [rejectFor, setRejectFor] = useState(null);
  const [rejectReason, setRejectReason] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (statusFilter !== "All") params.status = statusFilter;
      const { data } = await api.get("/admin/withdrawals", { params });
      setItems(data);
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => {
    load();
  }, [load]);

  const submitPay = async () => {
    if (!payFor) return;
    setRowBusy((b) => ({ ...b, [payFor]: true }));
    try {
      await api.post(`/admin/withdrawals/${payFor}/pay`, { tx_paid: payTx.trim() || null });
      setPayFor(null);
      setPayTx("");
      await load();
    } catch (e) {
      alert(e?.response?.data?.detail || "Failed.");
    } finally {
      setRowBusy((b) => ({ ...b, [payFor]: false }));
    }
  };

  const submitReject = async () => {
    if (!rejectFor) return;
    if (rejectReason.trim().length < 2) {
      alert("Reason is required.");
      return;
    }
    setRowBusy((b) => ({ ...b, [rejectFor]: true }));
    try {
      await api.post(`/admin/withdrawals/${rejectFor}/reject`, { reason: rejectReason.trim() });
      setRejectFor(null);
      setRejectReason("");
      await load();
    } catch (e) {
      alert(e?.response?.data?.detail || "Failed.");
    } finally {
      setRowBusy((b) => ({ ...b, [rejectFor]: false }));
    }
  };

  const filtered = items.filter((i) => {
    if (!search) return true;
    const s = search.toLowerCase();
    return (
      (i.user_email || "").toLowerCase().includes(s) ||
      (i.user_name || "").toLowerCase().includes(s) ||
      (i.id || "").toLowerCase().includes(s)
    );
  });

  return (
    <AdminLayout title="Withdrawals" subtitle="Pay outgoing requests">
      <div className="bb-card rounded-2xl overflow-hidden">
        <div className="flex flex-wrap items-center justify-between gap-3 p-4 border-b border-white/5">
          <div className="flex items-center gap-1 bg-white/[0.04] rounded-lg p-1 border border-white/8 overflow-x-auto">
            {["Pending", "Completed", "Rejected", "All"].map((s) => (
              <button
                key={s}
                onClick={() => setStatusFilter(s)}
                className={`px-3 py-1.5 text-[12px] rounded-md whitespace-nowrap transition-colors ${
                  statusFilter === s ? "bg-white text-[#07080d] font-medium" : "text-white/55 hover:text-white"
                }`}
              >
                {s}
              </button>
            ))}
          </div>
          <div className="flex items-center gap-2">
            <div className="flex items-center gap-2 px-3 h-9 bg-white/[0.04] border border-white/8 rounded-lg">
              <Search size={14} className="text-white/40" />
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search email, name"
                className="bg-transparent outline-none text-[12px] text-white placeholder:text-white/35 w-48"
              />
            </div>
            <button onClick={load} className="w-9 h-9 rounded-lg border border-white/8 text-white/70 hover:text-white flex items-center justify-center">
              <RefreshCw size={13} className={loading ? "animate-spin" : ""} />
            </button>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-[13px] min-w-[1000px]">
            <thead>
              <tr className="text-[11px] uppercase tracking-wider text-white/40 font-semibold bg-white/[0.02]">
                <th className="text-left py-3 px-5">User</th>
                <th className="text-left py-3">Method</th>
                <th className="text-right py-3">Amount</th>
                <th className="text-left py-3">Destination</th>
                <th className="text-left py-3">Requested</th>
                <th className="text-left py-3">Status</th>
                <th className="text-right py-3 pr-5">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((w) => (
                <tr key={w.id} className="border-t border-white/5 hover:bg-white/[0.02]">
                  <td className="py-4 px-5">
                    <div className="text-white font-medium">{w.user_name}</div>
                    <div className="text-white/45 text-[11px]">{w.user_email}</div>
                    <div className="text-white/30 text-[10px] font-mono">{w.id}</div>
                  </td>
                  <td>
                    <span className="text-white capitalize">{w.method}</span>
                  </td>
                  <td className="text-right text-white font-semibold tabular-nums">${fmt(w.amount)}</td>
                  <td>
                    {w.destination ? (
                      <span className="text-white/65 text-[12px] font-mono">
                        {w.destination.length > 28 ? `${w.destination.slice(0, 14)}…${w.destination.slice(-6)}` : w.destination}
                      </span>
                    ) : (
                      <span className="text-white/30 text-[12px]">via Telegram</span>
                    )}
                    {w.tx_paid && (
                      <div className="text-[10px] text-emerald-400/70 mt-1">Paid TX: {w.tx_paid.slice(0, 16)}…</div>
                    )}
                  </td>
                  <td className="text-white/65 text-[11px]">
                    {new Date(w.created_at).toLocaleString(undefined, { dateStyle: "short", timeStyle: "short" })}
                  </td>
                  <td>
                    <span className={`inline-block text-[10px] uppercase tracking-wider font-semibold px-2 py-0.5 rounded ${statusStyle[w.status] || "text-white/55 bg-white/[0.05]"}`}>
                      {w.status}
                    </span>
                    {w.reject_reason && (
                      <div className="text-[10px] text-rose-300/70 mt-1 max-w-[160px] truncate" title={w.reject_reason}>
                        {w.reject_reason}
                      </div>
                    )}
                  </td>
                  <td className="text-right pr-5">
                    {w.status === "Pending" && (
                      <div className="inline-flex gap-1.5">
                        <button
                          onClick={() => { setPayFor(w.id); setPayTx(""); }}
                          disabled={rowBusy[w.id]}
                          className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-md bg-emerald-500/15 border border-emerald-500/30 text-emerald-400 hover:bg-emerald-500/25 text-[11px] font-medium disabled:opacity-50"
                        >
                          <Check size={12} /> Mark paid
                        </button>
                        <button
                          onClick={() => { setRejectFor(w.id); setRejectReason(""); }}
                          disabled={rowBusy[w.id]}
                          className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-md border border-white/10 text-white/70 hover:bg-rose-500/10 hover:border-rose-400 hover:text-rose-400 text-[11px] font-medium"
                        >
                          <X size={12} /> Reject
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {!loading && filtered.length === 0 && (
          <div className="py-16 text-center text-[13px] text-white/40">No withdrawals to show.</div>
        )}
      </div>

      {payFor && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={() => setPayFor(null)}></div>
          <div className="relative bb-card rounded-3xl p-6 w-full max-w-md">
            <div className="font-display font-bold text-[18px] mb-1">Mark as paid</div>
            <p className="text-[12px] text-white/55 mb-4">This will debit the user's balance and close the request.</p>
            <label className="text-[12px] text-white/55 mb-1.5 block">Outgoing TX ID / reference (optional)</label>
            <input
              value={payTx}
              onChange={(e) => setPayTx(e.target.value)}
              placeholder="Tron / BTC TX or bank reference"
              className="w-full h-11 px-3 bg-white/[0.02] border border-white/10 rounded-xl text-[13px] text-white outline-none focus:border-[#ff7a1a] placeholder:text-white/30 font-mono"
            />
            <div className="flex justify-end gap-2 mt-4">
              <button onClick={() => setPayFor(null)} className="px-4 py-2 rounded-lg border border-white/10 text-white/70 text-[13px]">Cancel</button>
              <button onClick={submitPay} className="px-4 py-2 rounded-lg bg-emerald-500 hover:bg-emerald-600 text-white text-[13px] font-medium">Confirm paid</button>
            </div>
          </div>
        </div>
      )}

      {rejectFor && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={() => setRejectFor(null)}></div>
          <div className="relative bb-card rounded-3xl p-6 w-full max-w-md">
            <div className="font-display font-bold text-[18px] mb-1">Reject withdrawal</div>
            <textarea
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              placeholder="Reason shown to the customer"
              rows={3}
              className="w-full px-3 py-2.5 bg-white/[0.02] border border-white/10 rounded-xl text-[13px] text-white outline-none focus:border-[#ff7a1a] placeholder:text-white/30"
            />
            <div className="flex justify-end gap-2 mt-4">
              <button onClick={() => setRejectFor(null)} className="px-4 py-2 rounded-lg border border-white/10 text-white/70 text-[13px]">Cancel</button>
              <button onClick={submitReject} className="px-4 py-2 rounded-lg bg-rose-500 hover:bg-rose-600 text-white text-[13px] font-medium">Confirm reject</button>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
};

export default AdminWithdrawals;
